import type { FC, ReactNode } from "react";
import { ActivityIndicator, Pressable } from "react-native";
import { View, Text, Icon } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { PushNotificationSwitchState } from "../../hooks/usePushNotificationSwitch";

const CHEVRON_SIZE = 20;

export interface PushSwitchControlProps {
  readonly active: boolean;
  readonly onToggle: () => void;
  readonly disabled: boolean;
}

export interface NotificationPushSwitchProps extends PushNotificationSwitchState {
  /** The toggle control itself — the app supplies its own switch so it matches the rest of its settings screens. */
  readonly renderSwitch: (props: PushSwitchControlProps) => ReactNode;
}

/**
 * Whether this device receives push at all — the device-level half of push,
 * next to the Push column's per-scope half. Sits at the top of the form, the
 * same way the SMS phone number field does for SMS.
 */
export const NotificationPushSwitch: FC<NotificationPushSwitchProps> = ({
  isEnabled,
  isLoading,
  isBusy,
  isDeniedAtOsLevel,
  onToggle,
  onOpenSystemSettings,
  renderSwitch,
}) => {
  const { theme } = useUnistyles();

  return (
    <View style={styles.container}>
      <View style={styles.row}>
        <View style={styles.text}>
          <Text textStyle="sm/medium">Push notifications</Text>
          <Text textStyle="xs/regular" color="textMuted">
            Allow this device to receive push notifications. Choose which sites and cameras notify you below.
          </Text>
        </View>
        {isLoading ?
          <ActivityIndicator accessibilityLabel="Loading notification status" color={theme.colors.text} />
        : renderSwitch({ active: isEnabled, onToggle, disabled: isBusy || isDeniedAtOsLevel })}
      </View>
      {isDeniedAtOsLevel && (
        <Pressable onPress={onOpenSystemSettings} accessibilityRole="button">
          <View style={styles.row}>
            <View style={styles.text}>
              <Text textStyle="sm/medium">Open System Settings</Text>
              <Text textStyle="xs/regular" color="textMuted">
                Enable push notifications in system settings. Tap to open.
              </Text>
            </View>
            <Icon name="chevronRight" size={CHEVRON_SIZE} color={theme.colors.textMuted} />
          </View>
        </Pressable>
      )}
    </View>
  );
};

const styles = StyleSheet.create((theme) => ({
  container: {
    gap: theme.spacing.lg,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
  },
  text: {
    flex: 1,
    gap: theme.spacing.xs,
  },
}));
